import { 
  addDays, 
  parse, 
  isBefore, 
  isAfter,
  isWithinInterval, 
  startOfDay, 
  isValid, 
  getDay,
  differenceInCalendarDays, 
  differenceInMinutes, 
} from "date-fns";
import { formatInTimeZone, toZonedTime } from "date-fns-tz";
import { DEFAULT_PIT_CONFIG } from 'state/constants';
import { parseUTCDate, 
  combineDateAndTime, 
  safeDifferenceInCalendarDays,  
} from 'state/helpers';


export const parseDate = (dateStr) => {
  let date = parse(dateStr, "dd/MM/yy", new Date());
  date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  return date = date.toISOString();
};

export const parseDateTime = (dateStr, timeStr) => {
  let date = parse(dateStr, "dd/MM/yy", new Date());
  date = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));              
  const datetime = combineDateAndTime(date, timeStr);
  
  return datetime;                
};

export const filterArrayByPeriod = (array, dateFieldName, periodStart, periodEnd) => {  
  const start = new Date(periodStart);
  const end = new Date(periodEnd);

  return array.filter(item => {
    const rawDate = item[dateFieldName];
    if (!rawDate) return false; 
    
    const date = new Date(rawDate);
    return date >= start && date < end;
  });
};

export const addNote = (currentNote, newMessage) => {
  return currentNote ? `${currentNote}; ${newMessage}`.trim() : newMessage;
};

export const countDependents = (dependents, startDate, endDate) => {   
  if (!Array.isArray(dependents) || dependents.length === 0) {
    return 0;
  }
  
  const start = new Date(startDate);
  const end = new Date(endDate);

  let dependentCount = 0;
  dependents.forEach(dependent => {
    const validSince = dependent.validSince ? new Date(dependent.validSince) : null;
    const validUntil = dependent.validUntil ? new Date(dependent.validUntil) : null;

    if (!validSince) return;

    const isOverlap =
      validSince <= end && // phụ thuộc bắt đầu trước khi kỳ tính kết thúc
      (!validUntil || validUntil >= start); // và chưa kết thúc hoặc kết thúc sau kỳ tính bắt đầu

    if (isOverlap) dependentCount++;
  })

  return dependentCount;
};

export const countWeekendDays = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isAfter(start, end)) {
    return { saturdayCount: 0, sundayCount: 0, nonWorkingDays: 0 };
  }

  let saturdayCount = 0;
  let sundayCount = 0;
  let cursor = start;

  while (!isAfter(cursor, end)) {
    const day = getDay(cursor); // 0 = Sunday, 6 = Saturday
    if (day === 6) saturdayCount++;
    if (day === 0) sundayCount++;
    cursor = addDays(cursor, 1);
  }

  const nonWorkingDays = saturdayCount + sundayCount;
  return { saturdayCount, sundayCount, nonWorkingDays };
};

export const countHolidaysAndWeekends = (
  employmentStartDate, 
  employmentEndDate, 
  startDate, 
  endDate, 
  holidays,
) => {
  const employmentStart = employmentStartDate ? new Date(employmentStartDate) : null;
  const employmentEnd = employmentEndDate ? new Date(employmentEndDate) : null;
  const start = new Date(startDate);
  const end = new Date(endDate);

  const overlapStart = employmentStart && isAfter(employmentStart, start) ? employmentStart : start;
  const overlapEnd = employmentEnd && isBefore(employmentEnd, end) ? employmentEnd : end;
  
  const weekendDates = new Set();
  const holidayDates = new Set();

  // --- Đếm weekend trong phạm vi ---
  let cursor = new Date(overlapStart);
  while (!isAfter(cursor, overlapEnd)) {
    const day = getDay(cursor); // 0 = CN, 6 = T7
    const ds = cursor.toISOString().split("T")[0];
    if (day === 6 || day === 0) weekendDates.add(ds);
    cursor = addDays(cursor, 1);
  }

  // --- Xử lý các holiday ---
  holidays.forEach((h) => {
    if (h.frequency === "once") {
      let d = new Date(h.startDate);
      while (!isAfter(d, h.endDate)) {
        if (!isBefore(d, overlapStart) && !isAfter(d, overlapEnd)) {
          holidayDates.add(d.toISOString().split("T")[0]);
        }
        d = addDays(d, 1);
      }
    } else if (h.frequency === "yearly") {
      let d = new Date(overlapStart);
      while (!isAfter(d, overlapEnd)) {
        const hs = new Date(h.startDate);
        if (d.getDate() === hs.getDate() && d.getMonth() === hs.getMonth()) {
          holidayDates.add(d.toISOString().split("T")[0]);
        }
        d = addDays(d, 1);
      }
    }
  });

  // --- Loại trùng (holiday trùng weekend) ---
  const mergedDaysOff = new Set([...weekendDates, ...holidayDates]);

  // --- Tính toán kết quả ---
  const saturdayCount = [...weekendDates].filter((d) => getDay(new Date(d)) === 6).length;
  const sundayCount = [...weekendDates].filter((d) => getDay(new Date(d)) === 0).length;

  return {
    saturdayCount,
    sundayCount,
    weekendCount: weekendDates.size,
    holidayCount: holidayDates.size,
    nonWorkingDays: mergedDaysOff.size,
    daysOffList: Array.from(mergedDaysOff).sort(),
  };
};

export const buildHolidayDateSet = (holidays, startDate, endDate) => {
  const set = new Set();
  holidays.forEach((h) => {
    if (h.frequency === "once") {
      let d = new Date(h.startDate);
      const hEnd = new Date(h.endDate || h.startDate);
      while (!isAfter(d, hEnd)) {
        set.add(formatInTimeZone(d, 'UTC', 'yyyy-MM-dd'));
        d = addDays(d, 1);
      }
    } else if (h.frequency === "yearly") {
      let d = new Date(startDate);
      const end = new Date(endDate);
      while (!isAfter(d, end)) {
        const hs = new Date(h.startDate);
        if (d.getUTCDate() === hs.getUTCDate() && d.getUTCMonth() === hs.getUTCMonth()) {
          set.add(formatInTimeZone(d, 'UTC', 'yyyy-MM-dd'));
        }
        d = addDays(d, 1);
      }
    }
  });
  return set;
};

export const calcFlyingOnHolidays = (
  data, 
  holidayDateSet, 
  atdDate, 
  ataDate, 
  periodStart, 
  periodEnd
) => {    
  const flyingDates = new Set();
  for (const row of data) {
    if (!row.flightNo || !row.sector || row.pax) continue;

    const atd = row[atdDate] ? new Date(row[atdDate]) : null;
    const ata = row[ataDate] ? new Date(row[ataDate]) : null;
    
    if (!atd || !ata) continue;

    const isAtdWithin = atd >= periodStart && atd < periodEnd;
    const isAtaWithin = ata >= periodStart && ata < periodEnd;

    if (!isAtdWithin && !isAtaWithin) continue;

    const atdStr = formatInTimeZone(atd, 'UTC', 'yyyy-MM-dd');
    const ataStr = formatInTimeZone(ata, 'UTC', 'yyyy-MM-dd');
    if (holidayDateSet.has(atdStr)) flyingDates.add(atdStr);
    if (holidayDateSet.has(ataStr)) flyingDates.add(ataStr);
  }
  return flyingDates.size;
};

export const checkIsTerminated = (employmentEndDate, endDate) => {
  const employmentEnd = employmentEndDate ? new Date(employmentEndDate) : null;
  const end = new Date(endDate);

  if (!employmentEnd) return false;

  if (isBefore(employmentEnd, end) || employmentEnd.getTime() === end.getTime()) {
    return true;
  }

  // Nếu nghỉ việc sau kỳ tính → vẫn đang làm
  return false;
};

export const getCrewPostBase = (crewQuals, ranks, date) => {
  if (!Array.isArray(crewQuals) || !Array.isArray(ranks) || !date) return null;

  const targetDate = new Date(date);

  // Create quick lookup for rankPriority by rankCode (post)
  const rankMap = Object.fromEntries(
    ranks.map(r => [r.rankCode, r.rankPriority])
  );

  // Filter by date range
  const validQuals = crewQuals.filter(qual => {
    const beg = qual.begDay ? new Date(qual.begDay) : null;
    const end = qual.endDay ? new Date(qual.endDay) : null;
    return (
      !beg || // begDay is null
      (targetDate >= beg && (!end || targetDate <= end))
    );
  });

  if (validQuals.length === 0) return null;

  // Find qual with smallest rankPriority
  let bestQual = null;
  let bestPriority = Infinity;

  for (const qual of validQuals) {
    const pri = rankMap[qual.post] ?? Infinity; // If post not found in ranks, treat as lowest priority
    if (pri < bestPriority) {
      bestPriority = pri;
      bestQual = qual;
    }
  }

  return bestQual ? { post: bestQual.post, base: bestQual.base } : null;
}

export const getActiveJobTitles = (jobTitles = [], dateOrStart, maybeEnd) => {
  if (!Array.isArray(jobTitles)) return [];

  // Xác định mode: 1 ngày hay khoảng ngày
  const isRange = maybeEnd !== undefined;
  const startDate = isRange ? dateOrStart : new Date(dateOrStart ?? new Date());
  const endDate = isRange ? new Date(maybeEnd) : null;

  return jobTitles?.map((jt) => {
    const from = new Date(jt.effectiveFrom);
    const to = jt.effectiveTo ? new Date(jt.effectiveTo) : null;

    let overlapStart, overlapEnd, activeDays = 0;

    if (isRange) {
      // Kiểm tra khoảng giao nhau
      if (from <= endDate && (to === null || startDate <= to)) {
        // xác định phần giao giữa 2 khoảng: [from,to] và [startDate,endDate]
        overlapStart = from > startDate ? from : startDate;
        overlapEnd = to && to < endDate ? to : endDate;

        if (overlapStart && overlapEnd) {
          activeDays = differenceInCalendarDays(overlapEnd, overlapStart) + 1;
        } else if (!to) {
          // nếu to = null thì dùng endDate làm mốc kết thúc
          activeDays = differenceInCalendarDays(endDate, overlapStart) + 1;
        }
      }
    } else {
      // Kiểm tra hiệu lực tại 1 thời điểm
      if (from <= startDate && (to === null || startDate <= to)) {
        overlapStart = startDate;
        overlapEnd = startDate;
        activeDays = 1; // chỉ 1 ngày tại thời điểm đó
      }
    }

    return {
      ...jt,
      start: overlapStart,
      end: overlapEnd,
      activeDays,
    };
  })
  .filter((jt) => jt.start && jt.end && jt.activeDays > 0);
}

export const calcActiveDays = (employmentStartDate, employmentEndDate, startDate, endDate) => {
  if (!startDate || !endDate) return 0;

  let activeDays = 0;

  if (
    (employmentStartDate === null || employmentStartDate <= endDate) &&
    (employmentEndDate === null || startDate <= employmentEndDate)
  ) {
    const overlapStart = employmentStartDate && employmentStartDate > startDate
      ? employmentStartDate
      : startDate;

    const overlapEnd = employmentEndDate && employmentEndDate < endDate 
      ? employmentEndDate 
      : endDate;

    if (overlapStart && overlapEnd && overlapEnd >= overlapStart) {
      activeDays = safeDifferenceInCalendarDays(overlapEnd, overlapStart) + 1;
    }
  }

  return activeDays;
};

export const buildPayables = (costItems, activeJobTitles) => {
  const payables = {};

  costItems.forEach(ci => {
    const applicable = ci.applicableTo?.find(app =>
      activeJobTitles.some(jt => app.jobTitle.includes(jt))
    );

    payables[ci.costItemCode] = {
      value: applicable?.value || 0,
      category: ci.category,
      frequency: ci.frequency,
      formula: ci.formula,
      excludeIfResigned: ci.excludeIfResigned,
    };
  });

  return payables;
};

/**
 * Đếm số ngày nằm trong khoảng start–end của mỗi job
 * @param {Array} activeJobs - Danh sách job có start, end
 * @param {Array} dates - Danh sách ngày dạng "YYYY-MM-DD"
 * @param {String} fieldName - Tên trường cần ghi kết quả (VD: "workingDays" hoặc "unpaidDays")
 */
export const addDaysCount = (activeJobs = [], dates = new Map(), fieldName = "daysCount") => {
  if (!Array.isArray(activeJobs) || activeJobs.length === 0) return [];

  const dayEntries = dates instanceof Map
    ? Array.from(dates.entries()) // [[dayStr, { workingDays }], ...]
    : Array.from(dates || []).map(d => [d, { workingDays: 1 }]); // fallback cho Set hoặc mảng ngày

  return activeJobs.map((job) => {
    const { start, end } = job;
    if (!start || !end) return { ...job, [fieldName]: 0 };

    let total = 0;

    for (const [dayStr, { workingDays }] of dayEntries) {
      const day = new Date(dayStr);
      if (isValid(day) && isWithinInterval(day, { start, end })) {
        total += workingDays || 0;
      }
    }

    return { ...job, [fieldName]: total };
  });
};

export const addFlyingDays = (
  cur,
  periodStart,
  periodEnd,
  MINIMUM_OVERLAP_WORKDAY_MINUTES = 120,
  DEBRIEFING_MINUTES = 15,
  flyingDaysMap,
  CREW_PAYROLL_TIME_MODE = "UTC",
) => {
  let atdDate = "atdUTCDate";
  let ataDate = "ataUTCDate";
  if (CREW_PAYROLL_TIME_MODE === "LCL") {    
    atdDate = "atdLCLDate";
    ataDate = "ataLCLDate";
  }

  const crewCode = cur.crewCode;
  const atd = cur[atdDate] ? new Date(cur[atdDate]) : null;
  const ata = cur[ataDate] ? new Date(cur[ataDate]) : null;

  if (!crewCode || !atd || !ata) return;

  let workingDays = 1;
  if (cur.pax) workingDays = 0.5;

  const isAtdWithin = atd >= periodStart && atd < periodEnd;
  const isAtaWithin = ata >= periodStart && ata < periodEnd;

  if (!(isAtdWithin || isAtaWithin)) return;

  if (!flyingDaysMap.has(crewCode)) {
    flyingDaysMap.set(crewCode, new Map());
  }

  const daysMap = flyingDaysMap.get(crewCode);
  const addDay = (date, workingDays) => {
    const key = formatInTimeZone(date, "UTC", "yyyy-MM-dd");
    const existing = daysMap.get(key);

    const isDateWithin = date >= periodStart && date < periodEnd;
    if (isDateWithin && (!existing || existing.workingDays < workingDays)) {
      daysMap.set(key, { workingDays });
    }
  };

  addDay(atd, workingDays);

  if (isAtaWithin) {
    const daysDiff = differenceInCalendarDays(
      toZonedTime(ata, "UTC"), 
      toZonedTime(atd, "UTC")
    );
    
    if (daysDiff > 0) {
      const end = toZonedTime(ata, "UTC");
      const startOfDayUTC = startOfDay(end);

      const minutesDiff = differenceInMinutes(end, startOfDayUTC);
      const dutyMinutes = minutesDiff + DEBRIEFING_MINUTES; 
      
      if (dutyMinutes >= MINIMUM_OVERLAP_WORKDAY_MINUTES) {
        addDay(ata, workingDays);
      }
    }
  }
};
        
export const calcDutyOnDayOff = (
  rostersByCrew, 
  periodStart, 
  periodEnd, 
  crewScheduleCodes = [], 
  INSTRUCTOR_LINE_TRAINING_CODES,
  CREW_PAYROLL_TIME_MODE = "UTC",
) => {
  let atdDate = "atdUTCDate";
  // let ataDate = "ataUTCDate";
  if (CREW_PAYROLL_TIME_MODE === "LCL") {    
    atdDate = "atdLCLDate";
    // ataDate = "ataLCLDate";
  }

  let bhOnDayOff = 0;
  let sectorsOnDayOff = 0;
  let lineInstructorBHOnDayOff = 0; 
  let lineInstructorSectorsOnDayOff = 0;
  const tripCodeOnDayOff = {};

  // Lọc và nhóm sector trong kỳ
  const sectorsByDay = new Map();
  for (const r of rostersByCrew) {
    if (!r.sector || !r[atdDate]) continue;
    const atd = new Date(r[atdDate]);
    if (isNaN(atd)) continue;
    if (atd < periodStart || atd >= periodEnd) continue;

    const day = atd.toISOString().slice(0, 10);
    if (!sectorsByDay.has(day)) sectorsByDay.set(day, []);
    sectorsByDay.get(day).push(r);
  }

  // Xác định các ngày có paid day off trong kỳ
  const paidDayOffDays = new Set();
  for (const r of rostersByCrew) {
    if (!(r.paidDayOffs > 0 && r[atdDate])) continue;
    const atd = new Date(r[atdDate]);
    if (isNaN(atd)) continue;
    if (atd < periodStart || atd >= periodEnd) continue;
    paidDayOffDays.add(atd.toISOString().slice(0, 10));
  }

  // Duyệt từng ngày nghỉ có paid day off
  for (const day of paidDayOffDays) {
    const sameDaySectors = sectorsByDay.get(day) || [];
    if (sameDaySectors.length > 0) {
      for (const s of sameDaySectors) {
        bhOnDayOff += s.payableBlockTime || 0;
        sectorsOnDayOff += 1;
        if (INSTRUCTOR_LINE_TRAINING_CODES?.includes(s.trainingDutyIndicator)) {
          lineInstructorBHOnDayOff = s.payableBlockTime;
          lineInstructorSectorsOnDayOff += 1;
        }
      }
    } else {
      // Không có sector, kiểm tra schedule code trong ngày đó
      for (const r of rostersByCrew) {
        const rDay = r[atdDate] ? new Date(r[atdDate]).toISOString().slice(0, 10) : null;
        if (rDay !== day) continue;
        if (r.tripTypeCode && crewScheduleCodes.includes(r.tripTypeCode)) {
          const key = `${r.tripTypeCode}OnDayOff`;
          tripCodeOnDayOff[key] = (tripCodeOnDayOff[key] || 0) + 1;
        }
      }
    }
  }

  return { 
    bhOnDayOff, 
    sectorsOnDayOff, 
    lineInstructorBHOnDayOff, 
    lineInstructorSectorsOnDayOff, 
    tripCodeOnDayOff 
  };
}

export const accumulateLineInstructorTime = ({
  cur,
  member,
  startDate,
  endDate,
  accumulatedPayableHours,
  excessLineInstructorTime,
  fcRanks,
  FLIGHT_CREW_EXCESS_BLOCK_HOURS,
  CABIN_CREW_EXCESS_BLOCK_HOURS,
  INSTRUCTOR_LINE_TRAINING_CODES,
  CREW_PAYROLL_TIME_MODE = "UTC",
}) => {
  let atdDate = "atdUTCDate";
  // let ataDate = "ataUTCDate";
  if (CREW_PAYROLL_TIME_MODE === "LCL") {    
    atdDate = "atdLCLDate";
    // ataDate = "ataLCLDate";
  }
  
  const atd = parseUTCDate(toZonedTime(cur[atdDate], "UTC"));
  const inRange = isWithinInterval(atd, { start: startDate, end: endDate });

  if (!inRange) return { accumulatedPayableHours, excessLineInstructorTime };

  const payableBlockTime = cur.payableBlockTime || 0;
  const threshold = fcRanks.includes(member.crewRank)
    ? FLIGHT_CREW_EXCESS_BLOCK_HOURS
    : CABIN_CREW_EXCESS_BLOCK_HOURS;

  const before = accumulatedPayableHours;
  const after = before + payableBlockTime;
  accumulatedPayableHours = after;

  if (after > threshold && INSTRUCTOR_LINE_TRAINING_CODES?.includes(cur.trainingDutyIndicator)) {
    // Nếu trước khi cộng chưa vượt, chỉ cộng phần dư
    if (before < threshold) {
      excessLineInstructorTime += after - threshold;
    } else {
        // Nếu trước đó đã vượt, cộng toàn bộ blockTime
      excessLineInstructorTime += payableBlockTime;
    }
  }

  return { accumulatedPayableHours, excessLineInstructorTime };
}

export const accumulateOverStats = (
  cur,
  startDate,
  endDate,
  overStats,
  bhThresholds,
  accumulatedBlockHoursRef,
  airportPairMap,
  CREW_PAYROLL_TIME_MODE = "UTC",
) => {
  let atdDate = "atdUTCDate";
  // let ataDate = "ataUTCDate";
  if (CREW_PAYROLL_TIME_MODE === "LCL") {    
    atdDate = "atdLCLDate";
    // ataDate = "ataLCLDate";
  }

  const atd = parseUTCDate(toZonedTime(cur[atdDate], "UTC"));
  const inRange = isWithinInterval(atd, { start: startDate, end: endDate });
  if (!inRange) return;

  if (cur.pax || !cur.sector) return;

  const before = accumulatedBlockHoursRef.value;
  const after = before + cur.payableBlockTime;
  accumulatedBlockHoursRef.value = after;

  const type = airportPairMap.get(cur.sector) || "Default";

  for (const th of bhThresholds) {
    if ((before < th && after >= th) || (before >= th && after > th)) {
      overStats[`sectorsOver${th}`] += 1;
      overStats[`sectorsOver${th}Type_${type}`] += 1;
    }

    if (after <= th || (before < th && after >= th)) {
      overStats[`sectorsUnder${th}`] += 1;
      overStats[`sectorsUnder${th}Type_${type}`] += 1;
    }
  }
};

// -------------------------
// 1️⃣ Xác định loại PAX
// -------------------------
export const determinePaxType = (rostersByCrew, cur, index, positioningRule="AFB") => {
  if (!cur.pax) return null;

  // Out of base is POS, the remaining is DHD
  if (positioningRule !== "OMA") {
    if (cur.arrivalAirport !== cur.crewBase) return "POS";
  }

  const next = rostersByCrew[index + 1];
  
  if (
    index === rostersByCrew.length - 1 ||
    !next?.sector ||
    cur.arrivalAirport !== next.departureAirport
  ) {
    return "DHD";
  }

  if (cur.debriefUTCDate && next?.reportUTCDate) {
    return "DHD";
  }
  
  return "POS";
}

// -------------------------
// 2️⃣ Cộng dồn giờ & sector PAX trong kỳ
// -------------------------
export const accumulatePaxStats = (
  cur, 
  startDate, 
  endDate, 
  paxStats,
  CREW_PAYROLL_TIME_MODE = "UTC",
) => {
  let atdDate = "atdUTCDate";
  // let ataDate = "ataUTCDate";
  if (CREW_PAYROLL_TIME_MODE === "LCL") {    
    atdDate = "atdLCLDate";
    // ataDate = "ataLCLDate";
  }

  const atd = parseUTCDate(toZonedTime(cur[atdDate], "UTC"));
  const isAtdWithinPeriod = isWithinInterval(atd, { start: startDate, end: endDate });
  
  if (!isAtdWithinPeriod) return;

  if (cur.paxType === "DHD") {
    paxStats.dhdTime += cur.paxTime;
    paxStats.dhdSectors += cur.paxSectors;
  } else if (cur.paxType === "POS") {
    paxStats.posTime += cur.paxTime;
    paxStats.posSectors += cur.paxSectors;
  }
}

// -------------------------
// 3️⃣ Kiểm tra TAT và Perdiem
// -------------------------
export const calcPerdiem = (
  pre, 
  cur, 
  perdiemConfig,
  addNote,
) => {
  const {
    PER_DIEM_CALC_METHOD,
    MINIMUM_TAT_MINUTES,
    MINIMUM_PERDIEM_MINUTES,
    MAXIMUM_PERDIEM_MINUTES,
    // UNPAID_CODES,
    domesticAirportSet,
    airportMap,
    periodStart,
    periodEnd,
  } = perdiemConfig;

  if (
    !pre.sector || 
    !cur.sector ||
    pre.tripTypeCode !== cur.tripTypeCode ||
    pre.arrivalAirport !== cur.departureAirport ||
    cur.departureAirport === cur.crewBase 
    // || (UNPAID_CODES.includes(pre.trainingDutyIndicator) &&
    //   UNPAID_CODES.includes(cur.trainingDutyIndicator))
  ) {
    return;
  }

  if (pre.legSequenceNumber && 
    cur.legSequenceNumber && 
    cur.legSequenceNumber - pre.legSequenceNumber !== 1
  ) {
    return;
  }

  const breakStart = pre.ataUTCDate ? new Date(pre.ataUTCDate) : null;
  const breakEnd = cur.atdUTCDate ? new Date(cur.atdUTCDate) : null;
  const breakMinutes = differenceInMinutes(breakEnd, breakStart);
  
  const addPerdiem = (row, val, arrivalAirport) => {
    const isDomestic = domesticAirportSet.has(arrivalAirport);
    const arrTravelZone = airportMap.get(arrivalAirport)?.travelZone || null;
    if (isDomestic) {
      row.domPerdiem = val;
      row[`${arrTravelZone}_Perdiem`] = val;
    } else {
      row.intPerdiem = val;
      row[`${arrTravelZone}_Perdiem`] = val;
    }
  };

  // ✅ Check TAT
  if (breakMinutes < MINIMUM_TAT_MINUTES) {
    cur.note = addNote(cur.note, `TAT ${breakMinutes} mins`);
  }

  // ✅ Check Perdiem
  if (breakMinutes > MINIMUM_PERDIEM_MINUTES) {
    if (breakMinutes > MAXIMUM_PERDIEM_MINUTES) {
      cur.note = addNote(cur.note, `Perdiem ${(breakMinutes / 60).toFixed(1)} hrs`);
    } else {      
      if (PER_DIEM_CALC_METHOD === "HOURLY") {
        if (!(breakStart >= periodEnd || breakEnd <= periodStart)) {
          const perdiemStart = breakStart < periodStart ? periodStart : breakStart;
          const perdiemEnd = breakEnd > periodEnd ? periodEnd : breakEnd;

          if (perdiemEnd > perdiemStart) {
            const perdiemHours = differenceInMinutes(perdiemEnd, perdiemStart) / 60;
            if (breakEnd > periodEnd) {
              pre.perdiem = perdiemHours;
              addPerdiem(pre, perdiemHours, pre.arrivalAirport);
            } else {
              cur.perdiem = perdiemHours;
              addPerdiem(cur, perdiemHours, pre.arrivalAirport);
            }
          }
        }
      } else if (PER_DIEM_CALC_METHOD === "DAILY") {        
        if (breakStart >= periodStart && 
          breakStart <= periodEnd && 
          breakEnd > breakStart
        ) {                  
          const PER_DIEM_BLOCK_MINUTES = 720; // 12h
          const PER_DIEM_BLOCK_DAYS = 0.5;

          const blocks = Math.floor(breakMinutes / PER_DIEM_BLOCK_MINUTES);
          const perdiemDays = PER_DIEM_BLOCK_DAYS + blocks * PER_DIEM_BLOCK_DAYS;
          
          if (breakEnd > periodEnd) {
            pre.perdiem = perdiemDays;
            addPerdiem(pre, perdiemDays, pre.arrivalAirport);
          } else {
            cur.perdiem = perdiemDays;
            addPerdiem(cur, perdiemDays, pre.arrivalAirport);
          }          
        }
      }
    }
  }
}

// -------------------------
// 4️⃣ Ghi nhận ngày Timesheet và Unpaid
// -------------------------
export const mapScheduleCodes = (
  cur, 
  daysMaps, 
  periodStart, 
  periodEnd, 
  crewScheduleCodeMap,
  CREW_PAYROLL_TIME_MODE = "UTC",
) => {
  const { timesheetDaysMap, unpaidDaysMap } = daysMaps;

  let atdDate = "atdUTCDate";
  let ataDate = "ataUTCDate";
  if (CREW_PAYROLL_TIME_MODE === "LCL") {    
    atdDate = "atdLCLDate";
    ataDate = "ataLCLDate";
  }

  const crewCode = cur.crewCode;
  const atd = cur[atdDate] ? new Date(cur[atdDate]) : null;
  const ata = cur[ataDate] ? new Date(cur[ataDate]) : null;

  const isAtdWithin = atd >= periodStart && atd < periodEnd;
  const isAtaWithin = ata >= periodStart && ata < periodEnd;
  if (!(isAtdWithin || isAtaWithin)) return;
  
  const codeInfo =
    crewScheduleCodeMap.get(cur.tripTypeCode) ||
    crewScheduleCodeMap.get(cur.flightNo);

  if (!codeInfo) return;
  
  const addDay = (map, crewCode, date, workingDays) => {
    if (!map.has(crewCode)) map.set(crewCode, new Map());
    const daysMap = map.get(crewCode);
    const key = formatInTimeZone(date, "UTC", "yyyy-MM-dd");
    const existing = daysMap.get(key);

    const isDateWithin = date >= periodStart && date < periodEnd;
    if (isDateWithin && (!existing || existing.workingDays < workingDays)) {
      daysMap.set(key, { workingDays });
    }
  };

  // Timesheet
  if (codeInfo.isTimesheet) {
    addDay(timesheetDaysMap, crewCode, atd, codeInfo.workingDays);
    addDay(timesheetDaysMap, crewCode, ata, codeInfo.workingDays);
  }

  // Unpaid
  if (!codeInfo.isPaid) {
    addDay(unpaidDaysMap, crewCode, atd, codeInfo.workingDays);
    addDay(unpaidDaysMap, crewCode, ata, codeInfo.workingDays);
  }
};


/**
 * Tính toán tổng hoặc nối chuỗi cho các cột trong một mảng dữ liệu trong khoảng thời gian
 * @param {Array} dataArray - Mảng dữ liệu nguồn (VD: processedRostersByCrew)
 * @param {Iterable} columns - Danh sách tên cột cần tính (Set hoặc Array)
 * @param {Date|String} startDate - Ngày bắt đầu
 * @param {Date|String} endDate - Ngày kết thúc
 * @param {String} [dateField='atdUTCDate'] - Tên trường ngày trong dataArray
 * @returns {Object} - Kết quả gồm tổng (nếu số) hoặc chuỗi nối (nếu text)
 */
export const calcDutyColumns = (
  dataArray = [], 
  columns = [], 
  startDate, 
  endDate, 
  dateField = "atdUTCDate",
) => {
  if (!Array.isArray(dataArray) || dataArray.length === 0) return {};

  return columns.reduce((acc, key) => {
    // Xác định cột dạng số hay chuỗi
    const isNumeric = dataArray.some(
      (row) => !isNaN(parseFloat(row[key])) && row[key] !== null
    );

    acc[key] = dataArray.reduce((sum, row) => {
      const rawDate = row[dateField];
      if (!rawDate) return sum;

      const date = parseUTCDate(toZonedTime(rawDate, "UTC"));
      const inRange = isWithinInterval(date, { start: startDate, end: endDate });

      if (!inRange) return sum;

      if (isNumeric) {
        return sum + (parseFloat(row[key]) || 0);
      } else {
        const value = row[key] || "";
        return sum ? `${sum}, ${value}` : value;
      }
    }, isNumeric ? 0 : "");

    if (isNumeric) acc[key] = parseFloat(acc[key]) || 0;

    return acc;
  }, {});
}

export const generateDaysColumns = (
  processedRostersByCrew, 
  startDate, 
  endDate, 
  MINIMUM_OVERLAP_WORKDAY_MINUTES = 120,
  DEBRIEFING_MINUTES = 15, 
  CREW_PAYROLL_TIME_MODE = "UTC",
) => {
  const columns = {};
  const daysDiff = differenceInCalendarDays(endDate, startDate); // 1 -> 30 = 29 daysDiff
  
  let atdDate = "atdUTCDate";
  let ataDate = "ataUTCDate";
  if (CREW_PAYROLL_TIME_MODE === "LCL") {    
    atdDate = "atdLCLDate";
    ataDate = "ataLCLDate";
  }
  
  for (let i = 0; i <= daysDiff; i++) {
    const date = formatInTimeZone(addDays(startDate, i), 'UTC', "yyyy-MM-dd"); 
    columns[date] = ""; 
  }
  
  processedRostersByCrew.forEach((row) => {
    const atd = parseUTCDate(toZonedTime(row[atdDate], "UTC"));
    const ata = parseUTCDate(toZonedTime(row[ataDate], "UTC"));
    const isAtdWithinPeriod = isWithinInterval(atd, { start: startDate, end: endDate });
    const isAtaWithinPeriod = isWithinInterval(ata, { start: startDate, end: endDate });
    
    if (isAtdWithinPeriod || isAtaWithinPeriod) {
      const atdKey = formatInTimeZone(row[atdDate], "UTC", "yyyy-MM-dd"); 
      const ataKey = formatInTimeZone(row[ataDate], "UTC", "yyyy-MM-dd"); 
      const tripCode = row.tripTypeCode || row.flightNo || "";

      // Check if tripTypeCode already exists; if not, append
      if (columns[atdKey] !== undefined && !columns[atdKey]?.includes(tripCode)) {
        columns[atdKey] = columns[atdKey]
          ? `${columns[atdKey]}, ${tripCode}`
          : tripCode;
      }

      // Whether a flight
      if (row.dutyDate && row.tripTypeCode) { // row.routeDate && row.routeNo
        if (isAtaWithinPeriod) {
          const daysDiff = differenceInCalendarDays(
            toZonedTime(row[ataDate], 'UTC'),
            toZonedTime(row[atdDate], 'UTC')
          );

          if (daysDiff > 0) {
            const end = toZonedTime(row[ataDate], 'UTC');
            const startOfDayUTC = startOfDay(end); 

            const minutesDiff = differenceInMinutes(end, startOfDayUTC);
            const dutyMinutes = minutesDiff + DEBRIEFING_MINUTES; 

            if (dutyMinutes > MINIMUM_OVERLAP_WORKDAY_MINUTES) {
              if (columns[ataKey] !== undefined && !columns[ataKey]?.includes(tripCode)) {
                columns[ataKey] = columns[ataKey]
                  ? `${columns[ataKey]}, ${tripCode}`
                  : tripCode;
              }
            }
          }
        }
      } else {
        if (columns[ataKey] !== undefined && !columns[ataKey]?.includes(tripCode)) {
          columns[ataKey] = columns[ataKey]
            ? `${columns[ataKey]}, ${tripCode}`
            : tripCode;
        }
      }
    }
  });

  return columns;
};                                    

/**
 * Tính chi tiết thu nhập dựa trên payables.
 * @param {Object} cm - CrewMember object (đã enrich với payables).
 * @param {Object} dutyColumns - Object chứa các biến đầu vào:
 *   { blockHours: 75, sectors: 28, etc }
 * @returns {{ breakdown: Object, incomeAmount: Number }}
 */
export const evalFormula = (formula, duty, code) => {
  try {
    if (!formula || typeof formula !== "string") return 0;

    // Bước 1: Xác định tất cả "tên biến" trong công thức (chỉ chữ/số/underscore)
    const safeFormula = formula.replace(/\b([A-Za-z_]\w*)\b/g, (varName) => {
    // Nếu varName có trong duty → giữ nguyên
      if (Object.prototype.hasOwnProperty.call(duty, varName)) {
        return `duty.${varName}`;
      }

      // Nếu varName là số hoặc từ khóa JS → giữ nguyên
      if (!isNaN(varName) || ["Math", "true", "false", "null", "undefined"].includes(varName)) {
        return varName;
      }

      // Nếu varName không tồn tại → thay bằng 0
      // console.warn(`Missing variable in formula for ${code}:`, varName);
      return "0";
    });

    // Bước 2: Thực thi công thức
    const fn = new Function("duty", `return ${safeFormula};`);
    const result = fn(duty);

    // Bước 3: Bảo đảm kết quả là số hợp lệ
      return isNaN(result) ? 0 : result;
  } catch (err) {
    console.error(`Error evaluating formula for ${code}:`, err.message);
    return 0;
  }
}

export const calcIncomeBreakdown = (payables, dutyColumns = {}, isTerminated = false) => {
  if (!payables || Object.keys(payables).length === 0) {
    return { 
      breakdownUnit: {}, 
      breakdownQuantity: {}, 
      breakdownAmount: {}, 
      incomeAmount: 0 
    };
  }

  const breakdownUnit = {};
  const breakdownQuantity = {};
  const breakdownAmount = {};
  let incomeAmount = 0;

  const safeNum = v => (isNaN(v) || v == null ? 0 : Number(v));
    
  Object.entries(payables).forEach(([codeItem, payable]) => {
    const { value = 0, formula, excludeIfResigned } = payable;
    
    let quantity = 0;   
    let amount = 0;      
    const val = safeNum(value);

    if (formula) {        
      const evaluation = evalFormula(formula, dutyColumns, codeItem);        
      if (!isNaN(evaluation) && evaluation > 0) {
        quantity = evaluation;   
        amount = val * safeNum(evaluation);
      } 
    } else {
      quantity = 1;
      amount = val;
    }
    
    if (excludeIfResigned && isTerminated) amount = 0;

    breakdownUnit[`${codeItem}_Unit`] = value;
    breakdownQuantity[`${codeItem}_Qty`] = quantity;
    breakdownAmount[`${codeItem}_Amount`] = amount;
    incomeAmount += amount;
  });

  return { 
    breakdownUnit, 
    breakdownQuantity, 
    breakdownAmount, 
    incomeAmount,
  };
}

/**
 * Tính chi tiết các khoản bảo hiểm (BHXH, BHYT, BHTN...)
 * 
 * @param {Object} contribution - Thông tin bảo hiểm: { contributionRate: [], maxContributionBase?: Number }
 * @param {Number} contributoryIncome - Thu nhập trước bảo hiểm
 * @returns {Object} 
 *    {
 *      contributionBreakdown,          // chi tiết từng loại bảo hiểm
 *      employeeContribution_Amount,    // tổng người lao động đóng
 *      employerContribution_Amount,    // tổng doanh nghiệp đóng
 *    }
 */
export const calcContributionBreakdown = (
  contribution = {}, 
  contributoryIncome = 0, 
  hasContributions = false,
) => {
  if (!hasContributions || !contribution || !contribution.contributionRate || contributoryIncome <= 0) {
    return {
      contributionBreakdown: {},
      employeeContribution_Amount: 0,
      employerContribution_Amount: 0,
    };
  }

  const contributionBreakdown = {};
  let employeeContribution_Amount = 0;
  let employerContribution_Amount = 0;

  const contributionRates = contribution.contributionRate || [];
  contributionRates.forEach((item) => {
    const {
      contributionCode,
      employeeRate = 0,
      employerRate = 0,
      minContributionBase = 0,
      maxContributionBase = 200000000,
    } = item;

    const minBase = Math.max(contributoryIncome, minContributionBase);
    const baseIncome = Math.min(minBase, maxContributionBase);
    
    
    // Số tiền người lao động & doanh nghiệp đóng
    const employeeAmount = Math.round(baseIncome * (employeeRate / 100));
    const employerAmount = Math.round(baseIncome * (employerRate / 100));

    // Cập nhật breakdown
    contributionBreakdown[contributionCode] = {
      baseIncome,
      employeeRate,
      employerRate,
      employeeAmount,
      employerAmount,
    };

    // Cộng dồn
    employeeContribution_Amount += employeeAmount;
    employerContribution_Amount += employerAmount;
  });


  return {
    contributionBreakdown,
    employeeContribution_Amount,
    employerContribution_Amount,
  };
};

export const toPitCalcFormat = (cfg) => ({
  validSince: cfg.validSince ?? null,
  validUntil: cfg.validUntil ?? null,
  personalDeduction: cfg.personalDeduction ?? 15500000,
  dependentDeduction: cfg.dependentDeduction ?? 6200000,
  brackets: (cfg.brackets ?? []).map(b => ({
    ...b,
    max: b.max === null ? Infinity : Number(b.max)
  })),
  rules: cfg.rules ?? [],
});

/**
 * Tính thuế TNCN theo biểu lũy tiến Việt Nam (2026)
 * @param {Number} taxableIncome - Thu nhập tính thuế sau khi trừ giảm trừ, bảo hiểm
 * @returns {Number} Số thuế phải nộp
 */
export const calcVietnamProgressiveTax = (taxableIncome, config = DEFAULT_PIT_CONFIG) => {
  if (!taxableIncome || taxableIncome <= 0) return 0;

  // x.max === null or Infinity = bậc cuối không giới hạn
  const b = config.brackets.find(x => x.max === null || 
    x.max === Infinity || 
    taxableIncome <= x.max
  );
  if (!b) return 0;
  return Math.round(taxableIncome * b.rate - b.deduction);
};

export const calcIncomeBeforeTax = (netIncome = 0, config = DEFAULT_PIT_CONFIG) => {
  netIncome = Number(netIncome) || 0;

  for (const r of config.rules) {
    if (netIncome > r.limit) {
      return Math.round((netIncome - r.subtract) / r.rate);
    }
  }

  return 0;
};

